import type { HostPublicProfile } from '@/mocks/hostProfile';

interface Props {
  profile: HostPublicProfile;
}

const badgeColors: Record<string, { bg: string; text: string; border: string; icon: string }> = {
  oro: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-300', icon: 'text-amber-400' },
  plata: { bg: 'bg-foreground-50', text: 'text-foreground-500', border: 'border-foreground-200', icon: 'text-foreground-300' },
  bronce: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-300', icon: 'text-orange-400' },
  ninguno: { bg: 'bg-background-50', text: 'text-foreground-400', border: 'border-foreground-100', icon: 'text-foreground-300' },
};

export default function HostTopFansSection({ profile }: Props) {
  const top3 = profile.topFans.slice(0, 3);
  const rest = profile.topFans.slice(3);

  if (profile.topFans.length === 0) return null;

  return (
    <section id="top-fans" className="scroll-mt-24">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-award-line text-accent-500"></i>
          Top seguidores
        </h2>
        <span className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">
          Top {profile.topFans.length}
        </span>
      </div>

      {/* Podium top 3 */}
      <div className="flex items-end justify-center gap-3 mb-6">
        {/* 2nd */}
        {top3[1] && (
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded-full border-2 border-foreground-200 overflow-hidden mb-2">
              <img src={top3[1].photoUrl} alt={top3[1].name} className="w-full h-full object-cover" />
            </div>
            <span className="text-[10px] font-medium text-foreground-600 text-center leading-tight">{top3[1].name.split(' ')[0]}</span>
            <div className="w-20 h-16 bg-foreground-100 rounded-t-lg mt-2 flex items-center justify-center relative">
              <span className="text-lg font-bold text-foreground-400">2</span>
            </div>
          </div>
        )}

        {/* 1st */}
        {top3[0] && (
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-3 border-amber-400 overflow-hidden mb-2">
                <img src={top3[0].photoUrl} alt={top3[0].name} className="w-full h-full object-cover" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center">
                <i className="ri-vip-crown-fill text-white text-[10px]"></i>
              </div>
            </div>
            <span className="text-[10px] font-bold text-foreground-900 text-center leading-tight">{top3[0].name.split(' ')[0]}</span>
            <div className="w-20 h-24 bg-accent-500 rounded-t-lg mt-2 flex items-center justify-center relative">
              <span className="text-xl font-bold text-white">1</span>
            </div>
          </div>
        )}

        {/* 3rd */}
        {top3[2] && (
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded-full border-2 border-orange-300 overflow-hidden mb-2">
              <img src={top3[2].photoUrl} alt={top3[2].name} className="w-full h-full object-cover" />
            </div>
            <span className="text-[10px] font-medium text-foreground-600 text-center leading-tight">{top3[2].name.split(' ')[0]}</span>
            <div className="w-20 h-12 bg-orange-100 rounded-t-lg mt-2 flex items-center justify-center relative">
              <span className="text-lg font-bold text-orange-400">3</span>
            </div>
          </div>
        )}
      </div>

      {/* Rest of list */}
      {rest.length > 0 && (
        <div className="space-y-1.5">
          {rest.map((fan) => {
            const c = badgeColors[fan.badge];
            return (
              <div key={fan.id} className="flex items-center gap-3 bg-white rounded-xl border border-foreground-100 p-3 hover:shadow-sm transition-all cursor-pointer">
                <span className="text-xs font-bold text-foreground-400 w-5 text-center">{fan.rank}</span>
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={fan.photoUrl} alt={fan.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-foreground-800 block truncate">{fan.name}</span>
                  <span className="text-[10px] text-foreground-400">{fan.username}</span>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-xs font-semibold text-foreground-900 block">{fan.score}</span>
                  <span className="text-[10px] text-foreground-400">pts</span>
                </div>
                {fan.badge !== 'ninguno' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border whitespace-nowrap ${c.bg} ${c.text} ${c.border}`}>
                    {fan.badge === 'oro' ? 'Oro' : fan.badge === 'plata' ? 'Plata' : 'Bronce'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}